

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { setAccessToken, setSessionId, clearAuth as clearAuthClient, setOnTokenRefreshed } from '@/lib/auth-client';

export interface User {
  id: string;
  email: string;
  username: string;
  avatarSeed: string;
  coins: number;
  gems: number;
  usdBalanceCents: number;
  level: number;
  xp: number;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  role: 'USER' | 'ADMIN';
  hasSeenTutorial: boolean;
  jestGuest: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setAuth: (accessToken: string, sessionId: string) => void;
  updateBalance: (balance: { coins?: number; gems?: number; usdBalanceCents?: number }) => void;
  updateXp: (xp: number, level: number) => void;
  markTutorialSeen: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      sessionId: null,
      isAuthenticated: false,
      isLoading: true,
      isHydrated: false,
      error: null,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        }),

      setAuth: (accessToken, sessionId) => {
        // Sync with direct localStorage for auth-client compatibility
        setAccessToken(accessToken);
        setSessionId(sessionId);
        set({
          accessToken,
          sessionId,
        });
      },

      updateBalance: (balance) =>
        set((state) => ({
          user: state.user
            ? {
                ...state.user,
                coins: balance.coins ?? state.user.coins,
                gems: balance.gems ?? state.user.gems,
                usdBalanceCents: balance.usdBalanceCents ?? state.user.usdBalanceCents,
              }
            : null,
        })),

      updateXp: (xp, level) =>
        set((state) => ({
          user: state.user
            ? {
                ...state.user,
                xp,
                level,
              }
            : null,
        })),

      markTutorialSeen: () =>
        set((state) => ({
          user: state.user
            ? {
                ...state.user,
                hasSeenTutorial: true,
              }
            : null,
        })),

      logout: () => {
        // Clear direct localStorage for auth-client compatibility
        clearAuthClient();
        set({
          user: null,
          accessToken: null,
          sessionId: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'fantasy-prime-auth',
      storage: createJSONStorage(() => localStorage),
      // Game-reload contract: the player should NOT be set on reload. Jest
      // owns identity — every page load re-authenticates fresh via
      // JestSDK.getPlayerSigned() → /api/auth/jest. Persisting accessToken /
      // sessionId across reloads would let a stale or wrong-player token
      // pose as the current player, which is also what was masking the
      // purchase-recovery hook (it was firing against the prior session's
      // token before the new sign-in completed).
      partialize: () => ({}),
      onRehydrateStorage: () => (state) => {
        // Belt-and-braces: clear the auth-client's parallel localStorage
        // entries too, in case any survived from before this change.
        clearAuthClient();
        state?.setHydrated();
      },
    }
  )
);

// Sync refreshed tokens from authFetch back into Zustand store
setOnTokenRefreshed((token) => {
  useAuthStore.setState({ accessToken: token });
});
